import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, CopyButton, LoadingState, Modal, RefreshIcon, SparkIcon, useToast } from "@signet/ui";
import { useCreatorPhotos } from "@signet/web-kit";
import { CreatorChip } from "../shared.tsx";
import { addLink, fetchLinkSource, STOCK, stockKeys, type Stock } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Adding the link to an account's bio (Jay, 2026-10-07). Signet copies the
   creator's newest IG SUICIDE link in Links; the poster decides only its
   slug, with the links editor's slug ideas a press away. Once made, the
   address is shown to copy straight into the bio.
   ────────────────────────────────────────────────────────────────────────── */

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");
const clean = (slug: string) => slug.trim().toLowerCase().replace(/^\/+/, "").replace(/[^a-z0-9._-]+/g, "-").replace(/-{2,}/g, "-");

export function LinkModal({ account, onClose }: { account: Stock | null; onClose: () => void }) {
  const [slug, setSlug] = useState("");
  // What was in the box when ideas were asked for, and which set of six.
  const [ask, setAsk] = useState<{ current: string; page: number } | null>(null);
  const [made, setMade] = useState<string | null>(null);
  const client = useQueryClient();
  const toast = useToast();
  const photos = useCreatorPhotos(STOCK);
  useEffect(() => {
    setSlug("");
    setAsk(null);
    setMade(null);
  }, [account?.id]);

  const source = useQuery({ queryKey: ["igsuicide", "stock", "link-source", account?.id], queryFn: () => fetchLinkSource(account!.id), enabled: Boolean(account), staleTime: 30_000 });
  const ideas = useQuery({
    queryKey: ["igsuicide", "stock", "link-ideas", account?.id, ask],
    queryFn: () => fetchLinkSource(account!.id, ask!),
    enabled: Boolean(account) && ask !== null,
    staleTime: Infinity,
  });
  const create = useMutation({
    mutationFn: (manual: boolean) => addLink(account!.id, manual ? null : clean(slug)),
    onSuccess: (r) => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      if (r.url) setMade(r.url);
      else {
        toast.success(`Link marked added on @${account!.username}`);
        onClose();
      }
    },
    onError: (error) => toast.error("Couldn't make the link", messageOf(error)),
  });

  if (!account) return null;
  const s = source.data?.source;
  // In a highlight after a flag: this is the account's new link (Jay, 2026-10-12).
  const fresh = Boolean(account.linkFlaggedOn);
  // Live off the sheet: already posting, so the link may go in a story as well as the bio.
  const sheet = Boolean(account.importedActive) && !fresh;
  const ready = clean(slug).length >= 2;

  if (made) {
    return (
      <Modal
        open
        onClose={onClose}
        title="Link made"
        description={sheet ? `@${account.username}'s link. It is saved on the account, so it stays on its card.` : `Put this in @${account.username}'s bio now, straight in the bio: no @ mention, no highlight.`}
        footer={<button type="button" className="button brass" onClick={onClose}>Done</button>}
      >
        <div className="igs-link-made">
          <CheckIcon size={20} />
          <CopyButton value={made} label="link" className="igs-copy igs-link-url">{made}</CopyButton>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={fresh ? `A new link for @${account.username}` : sheet ? `A link for @${account.username}` : `Add the link to @${account.username}`}
      description={
        account.creator
          ? <>{fresh ? "The flagged link stays where it is. This is a new " : "A "}copy of <CreatorChip name={account.creator} photos={photos} size={14} />&apos;s newest {source.data?.category ?? "IG SUICIDE"} link, at a {fresh ? "new " : ""}slug you choose.</>
          : undefined
      }
      footer={
        <>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="button" className="button brass" disabled={!s || !ready || create.isPending} onClick={() => create.mutate(false)}>
            {create.isPending ? "Making the link…" : "Make the link"}
          </button>
        </>
      }
    >
      {source.isLoading ? (
        <LoadingState />
      ) : !s ? (
        <div className="igs-link-form">
          <p className="igs-alert">
            {account.creator ? <CreatorChip name={account.creator} photos={photos} size={14} /> : "This creator"} has no {source.data?.category ?? "IG SUICIDE"} link in Links yet, so there is nothing to copy. Make one in Links first, or if you
            already put a link in the bio yourself, record it.
          </p>
          <button type="button" className="secondary-button" disabled={create.isPending} onClick={() => create.mutate(true)}>
            {fresh ? "I made a new link myself" : "I added a link myself"}
          </button>
        </div>
      ) : (
        <div className="igs-link-form">
          <p className="igs-link-source">
            Copying <strong>{s.label || s.url}</strong>
            <span>{s.url}</span>
          </p>

          <div className="igs-slug-note" role="note">
            <SparkIcon size={16} />
            <p>
              <strong>Take your time with this one.</strong> Find the most natural looking slug that makes sense for {account.creator ? <CreatorChip name={account.creator} photos={photos} size={14} /> : "this creator"}: something a real person would
              put in their own bio, not something that looks generated or spammy.
            </p>
          </div>

          <label className="igs-slug-label" htmlFor="igs-slug">Slug</label>
          <div className="input-prefix igs-slug-input">
            <span>{s.host}/</span>
            <input id="igs-slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="the slug" autoComplete="off" autoFocus />
          </div>

          <div className="igs-ideas">
            <button
              type="button"
              className="secondary-button"
              onClick={() => setAsk((a) => (a === null ? { current: clean(slug), page: 0 } : { current: a.current, page: a.page + 1 }))}
              disabled={ideas.isFetching}
            >
              {ask === null ? <SparkIcon size={14} /> : <RefreshIcon size={14} />}
              {ask === null ? "Recommend a slug" : "More ideas"}
            </button>
            {ideas.data?.ideas.length ? (
              <ul aria-label="Slug ideas">
                {ideas.data.ideas.map((idea) => (
                  <li key={idea}>
                    <button type="button" className={`igs-idea${clean(slug) === idea ? " on" : ""}`} onClick={() => setSlug(idea)}>
                      {idea}
                    </button>
                  </li>
                ))}
              </ul>
            ) : ask !== null && !ideas.isFetching ? (
              <span className="faint">No more ideas. Try typing a start and asking again.</span>
            ) : null}
          </div>
        </div>
      )}
    </Modal>
  );
}
